import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, User, Mail, Lock, Key, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

const InitialSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [alreadySetup, setAlreadySetup] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    setup_key: 'solar-platform-setup-2026'
  });

  // Check if platform is already setup
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await api.get('/setup-status');
        if (response.data?.is_configured) {
          setAlreadySetup(true);
        }
      } catch (error) {
        // If endpoint fails, assume not configured yet
        console.error('Setup status check failed:', error);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.first_name || !formData.last_name || 
        !formData.email || !formData.password) {
      toast.error('Vul alle velden in');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 tekens zijn');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/initial-setup', formData);
      setSuccess(true);
      toast.success('Platform setup voltooid!');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Setup mislukt';
      toast.error(message);
      
      if (message.includes('al geconfigureerd') || message.includes('already')) {
        setAlreadySetup(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (alreadySetup) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            </div>
            <CardTitle>Platform Al Geconfigureerd</CardTitle>
            <CardDescription>
              Dit platform is al geconfigureerd. Ga naar de login pagina om in te loggen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full">
              Ga naar Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            </div>
            <CardTitle>Setup Voltooid!</CardTitle>
            <CardDescription>
              Je Super Admin account is aangemaakt. Je wordt doorgestuurd naar de login pagina...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full">
              Nu Inloggen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Platform Setup</CardTitle>
          <CardDescription>
            Maak het eerste Super Admin account aan om het platform te beheren
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company_name">Bedrijfsnaam</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="company_name"
                  className="pl-10"
                  placeholder="Solar Engineering BV"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                />
              </div>
            </div>
            
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Voornaam</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="first_name"
                    className="pl-10"
                    placeholder="Cyril"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Achternaam</Label>
                <Input
                  id="last_name"
                  placeholder="Lambrechts"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  className="pl-10"
                  placeholder="cyril@solar-engineering.be"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>
            
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  className="pl-10"
                  placeholder="Minimaal 6 tekens"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
            </div>
            
            {/* Setup Key (hidden by default, shown if needed) */}
            <div className="space-y-2">
              <Label htmlFor="setup_key" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Setup Sleutel
              </Label>
              <Input
                id="setup_key"
                type="password"
                placeholder="Setup sleutel"
                value={formData.setup_key}
                onChange={(e) => setFormData({ ...formData, setup_key: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">
                De standaard sleutel is al ingevuld. Wijzig alleen als je een andere sleutel hebt ingesteld.
              </p>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">
                Dit account krijgt volledige toegang tot het platform, inclusief het beheren van alle bedrijven en gebruikers.
              </p>
            </div>
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Bezig met setup...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Platform Configureren
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Button variant="link" onClick={() => navigate('/login')}>
              Al een account? Login hier
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InitialSetup;
